import { DateTime } from 'luxon'
import { CognitoIdentityCredentials } from '@aws-sdk/credential-provider-cognito-identity/dist-types/fromCognitoIdentity'
import { fromCognitoIdentityPool } from '@aws-sdk/credential-providers'
import * as sentry from '@sentry/browser'
import { KinesisTracker } from '@yanolja-org/ya-tracker'
import cookies from 'browser-cookies'
import { v4 as uuidV4 } from 'uuid'
import conf from '~/app/conf'

const IdentityPoolId = conf.aws.credentials.IdentityPoolId
const region = conf.aws.region
let credentials: CognitoIdentityCredentials | undefined

/**
 * cognito 인증 정보를 받아오는 함수
 */
export async function getCredentials(): Promise<CognitoIdentityCredentials | undefined> {
  /**
   * PC WEB, Mobile Web 에서 열린 페이지는 aws-sdk 를 직접 사용하여 kinesis 로그전송
   */
  try {
    if (!credentials || isExpireCred(credentials)) {
      const credentialsProvider = fromCognitoIdentityPool({
        identityPoolId: IdentityPoolId,
        clientConfig: { region },
        userIdentifier: uuidV4(),
      })

      credentials = await credentialsProvider()
    }

    return credentials
  } catch (e) {
    console.error(e)
  }
  return
}

function isExpireCred(_credentials: CognitoIdentityCredentials | undefined) {
  if (!_credentials?.expiration) return true
  const expired = DateTime.fromJSDate(_credentials.expiration)
  return expired < DateTime.now()
}

export type pageNames =
  | 'Cart' // '장바구니 페이지'
  | 'CartOrder' // '통합주문 페이지'
  | 'CartOrderComplete' // '통합주문 완료 페이지'
  | 'CartOrderList' // '통합주문 목록 페이지'
  | 'CartOrderDetail' // '통합주문 상세 페이지'
  | 'CartOrderCancel' // '통합주문 취소 페이지'
  | 'CartOrderCancelComplete' //'통합주문 취소완료 페이지'
  | 'OrderComplete' // 국내 숙소 주문 완료 페이지
  | 'LeisureOrderComplete' // 국내 레저티켓 주문 완료 페이지
  | 'TrainOrderComplete' // 국내 기차 주문 완료 페이지
  | 'OrderList' // 국내 숙소 주문 목록 페이지
  | 'LeisureOrderList' // 국내 레저 주문 목록 페이지
  | 'TrainOrderList' // 국내 기차 주문 목록 페이지
  | 'LeisureOrderDetail' // 레저 주문 상세 페이지
  | 'TrainOrderDetail' // 국내 기차 상세 페이지
  | 'OrderCancel' // 국내 숙소 취소 페이지
  | 'LeisureOrderCancel' // 국내 레저티켓 취소 페이지
  | 'TrainOrderCancel' // 국내 기차 취소 페이지
  | 'LeisureOrderCancelComplete' // 국내 레저티켓 취소 완료 페이지
  | 'TrainOrderCancelComplete' // 국내 기차 취소 완료 페이지
  | 'Order' // 국내 숙소 주문 페이지
  | 'LeisureOrder' // 국내 레저티켓 주문 페이지
  | 'TrainOrder' // 국내 기차 주문 페이지
  | 'GlobalOrderStatus' // 해외주문 공통
  | 'GOrderList' // 해외 주문 내역
  | 'GOrderDetail' // 해외 주문 상세
  | 'GOrderComplete' // 해외 주문 완료
  | 'GPlaceVoucher' // 해외 주문 예약확인서
  | 'GlobalOrderCancel' // 해외 주문 취소 페이지
  | 'GlobalOrder' // 해외 주문서 페이지
  | 'GuestOrderSearch' // 비회원 예약조회 페이지 (sunny-web 동일 identifier)

type eventTypes = 'view' | 'click' | 'impr' | 'bg'
type primitiveType = string | number | boolean

export type KinesisLog = {
  pageName: pageNames
  eventType: eventTypes
  object?: primitiveType
  objectType?: primitiveType
  objectContainer?: string
} & { [key: string]: any }

/**
 * config 에서 env 를 반환한다.
 *
 * @returns LIVE | STAGE | QA | DEV
 */
const getEnv = (): 'LIVE' | 'STAGE' | 'QA' | 'DEV' => {
  const runMode = conf.environment
  if (runMode === 'prod') {
    return 'LIVE'
  }

  return runMode.toUpperCase() as 'STAGE' | 'QA' | 'DEV'
}

/**
 * 아래 필드 이외의 시스템 정보는 ya-tracker 를 통해 전송됨.
 * @link https://bitbucket.yanolja.in/projects/FES/repos/ya-tracker/browse/src/tracker/KinesisTracker.ts
 *
 * 주문번호 노출로 인해 currentUri 는 빈 값으로 전송
 */
const addSystemFields = data => {
  return {
    currentUri: '',
    env: getEnv(),
    webVersion: conf.version,
    cgntId: cookies.get('cgntId'),
    ...data,
  }
}

/**
 * @readme 외부 도메인으로 연결되는 함수 로그는 sendKinesisClickLogTimeoutAfter 사용
 */
export async function sendKinesisLog({ eventType, pageName, ...rest }: KinesisLog) {
  const data = addSystemFields(rest)
  switch (eventType) {
    case 'bg':
      KinesisTracker.bg(pageName, data)
      break
    case 'click':
      await KinesisTracker.click(pageName, data)
      break
    case 'impr':
      KinesisTracker.impression(pageName, data)
      break
    case 'view':
      KinesisTracker.view(pageName, data)
  }
}

export interface InitializeKinesisMeta {
  memberGrade: string
  hashedMemberNo: string
  nolMemberProxyId: string
}

/**
 * Ya-tracker에 회원 정보 등 랜딩 이후 처리가 필요한 값을 설정하고 초기화하는 함수
 */
export const initializeKinesisTracker = ({
  memberGrade,
  hashedMemberNo,
  nolMemberProxyId,
}: InitializeKinesisMeta): Promise<void> => {
  // Kinesis Tracker 초기화
  return KinesisTracker.initialize({
    IdentityPoolId: conf.aws.credentials.IdentityPoolId,
    streamName: conf.aws.kinesis.stream,
    ...(!!hashedMemberNo && { memberGrade, hashedMemberNo, nolMemberProxyId }),
    env: getEnv(),
    logTransform: kinesisLogData => ({
      ...kinesisLogData,
      currentUri: '',
    }),
    onError({ err, kinesisLogData, data }) {
      if (err) {
        sentry.withScope(scope => {
          scope.setExtra('payload', kinesisLogData)
          data && scope.setExtra('PutRecordsCommandOutput', data)
          sentry.captureException(err)
        })
      }
    },
  })
}
